import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthProvider';
import { msalInstance, loginRequest } from './authConfig';
import styles from './LoginPage.module.css';
import { AppStateContext } from '../../state/AppProvider';
import { buildMediaUrl } from '../../config/apiConfig';

const LoginPage: React.FC = () => {
  const { login, isLoading, resetAuth } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const appStateContext = useContext(AppStateContext)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [logoLoaded, setLogoLoaded] = useState(false)
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);

  // Check for session expired message on mount
  useEffect(() => {
    const reason = sessionStorage.getItem('session_expired_reason');
    if (reason) {
      setSessionExpiredMessage(reason);
      // Clear the flag so it doesn't show again on refresh
      sessionStorage.removeItem('session_expired_reason');
    }
  }, []);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const fs = appStateContext?.state.frontendSettings?.ui
        
        if (fs && fs.logo) {
          // Use buildMediaUrl to get the correct URL for the logo
          const targetLogoUrl = buildMediaUrl(fs.logo)
          
          // Add a timeout to prevent infinite loading
          const logoTimeout = setTimeout(() => {
            console.warn('Logo loading timed out after 5 seconds, proceeding without logo')
            setLogoLoaded(true) // Proceed with login even if logo fails
          }, 5000)
          
          // Preload the image to avoid flashing
          const img = new Image()
          img.onload = () => {
            clearTimeout(logoTimeout)
            setLogoUrl(targetLogoUrl)
            setLogoLoaded(true)
          }
          img.onerror = () => {
            // If the configured logo fails to load, proceed anyway
            clearTimeout(logoTimeout)
            console.error('Failed to load configured logo:', targetLogoUrl)
            console.info('Proceeding with login despite logo error')
            setLogoUrl(null) // Clear broken logo URL
            setLogoLoaded(true) // Allow login to proceed
          }
          img.src = targetLogoUrl
        } else {
          // No logo configured, proceed immediately
          setLogoLoaded(true)
        }
      } catch (e) {
        console.error('Error loading logo:', e)
        console.info('Proceeding with login despite logo error')
        setLogoLoaded(true) // Allow login to proceed
      }
    }
    
    // Only load logo when we have the frontend settings
    if (appStateContext?.state.frontendSettings) {
      loadLogo()
    } else {
      // If no frontend settings, start a backup timer
      const backupTimer = setTimeout(() => {
        console.warn('Frontend settings not loaded after 3 seconds, proceeding without logo')
        setLogoLoaded(true)
      }, 3000)
      
      return () => clearTimeout(backupTimer)
    }
  }, [appStateContext?.state.frontendSettings])

  const handleImageLoad = () => {
    setLogoLoaded(true)
  }

  const handleImageError = () => {
    console.error('Logo failed to load')
    setLogoLoaded(false)
  }

  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      await login();
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Direct MSAL test function
  const handleDirectMSALLogin = async () => {
    console.log('=== DIRECT MSAL LOGIN TEST ===');
    setIsLoggingIn(true);
    try {
      // Check if interaction is already in progress
      const accounts = msalInstance.getAllAccounts();
      console.log('Current accounts:', accounts);
      
      // Clear any existing interactions first
      if (accounts.length > 0) {
        console.log('Found existing accounts, checking if already authenticated...');
        msalInstance.setActiveAccount(accounts[0]);
        alert('User already has an active account!');
        setIsLoggingIn(false);
        return;
      }
      
      console.log('Calling MSAL directly...');
      const response = await msalInstance.loginPopup(loginRequest);
      console.log('Direct MSAL response:', response);
      alert('Direct MSAL login successful!');
    } catch (error: any) {
      console.error('Direct MSAL login failed:', error);
      
      if (error?.errorCode === 'interaction_in_progress') {
        alert('Interaction already in progress. Please wait and refresh the page if needed.');
      } else {
        alert('Direct MSAL login failed: ' + error?.message);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Force redirect login (bypasses popup issues completely)
  const handleRedirectLogin = async () => {
    console.log('=== USING REDIRECT LOGIN ===');
    setIsLoggingIn(true);
    try {
      console.log('Forcing redirect login...');
      await msalInstance.loginRedirect(loginRequest);
      // Note: This will redirect the page, so no code after this will execute
    } catch (error: any) {
      console.error('Redirect login failed:', error);
      alert('Redirect login failed: ' + error?.message);
      setIsLoggingIn(false);
    }
  };

  // Clear MSAL state and restart - AGGRESSIVE VERSION
  const handleClearMSAL = async () => {
    console.log('=== CLEARING MSAL STATE AGGRESSIVELY ===');
    setIsLoggingIn(true);
    try {
      // Use the new reset function
      await resetAuth();
      alert('MSAL completely reset. You can now try logging in again.');
    } catch (error) {
      console.error('Error clearing MSAL state:', error);
      alert('Error clearing MSAL state. Refreshing page...');
      window.location.reload();
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      {!logoLoaded ? (
        // Show loading state until logo is ready or timeout
        <div className={styles.loadingContainer}>
          <div className={styles.logoPlaceholder}>
            <div className={styles.spinner}></div>
            <p style={{marginTop: '10px', fontSize: '14px', color: '#666'}}>
              Loading...
            </p>
          </div>
        </div>
      ) : (
        // Show login form once logo is loaded (or failed/timed out)
        <div className={styles.loginCard}>
          <div className={styles.logoContainer}>
            {logoUrl ? (
              <img 
                src={logoUrl}
                alt="Bynet AI Agent" 
                className={styles.logo}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            ) : (
              // Fallback when no logo or logo failed to load
              <div className={styles.logoFallback}>
                <h2 style={{color: '#0078d4', margin: 0}}>Bynet AI Agent</h2>
              </div>
            )}
          </div>
          
          <div className={styles.loginContent}>
            {sessionExpiredMessage && (
              <div className={styles.sessionExpiredBanner}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink: 0}}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <div>
                  <strong>Session Expired</strong>
                  <p style={{margin: '4px 0 0 0', fontSize: '13px'}}>
                    You were logged out due to inactivity. Please sign in again.
                  </p>
                </div>
                <button 
                  onClick={() => setSessionExpiredMessage(null)} 
                  className={styles.dismissButton}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}
            <h1 className={styles.title}>Welcome to Bynet AI Agent</h1>
          <p className={styles.subtitle}>
            Please sign in with your Microsoft account to continue
          </p>
          
          <button 
            onClick={handleLogin}
            disabled={isLoggingIn || isLoading}
            className={styles.loginButton}
          >
            {(isLoggingIn || isLoading) ? (
              <>
                <span className={styles.spinner}></span>
                Signing in...
              </>
            ) : (
              <>
                <svg className={styles.microsoftIcon} viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="12" y="1" width="9" height="9" fill="#00a4ef"/>
                  <rect x="1" y="12" width="9" height="9" fill="#00a4ef"/>
                  <rect x="12" y="12" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </>
            )}
          </button>

          <p className={styles.securityNote}>
            🔒 Secured with Azure AD
          </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
